import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { showLoader, hideLoader } from "../../../store/common/slice";
import BllinkUtils from "../../../common/utils/utils";
import { calcMonthsTextForSelectBox, shortenText } from "../../../common/shared/statelessMethods";
import { constants } from "../../../common/constants/constants";
import { useAccessToken } from "../../../common/hooks/useAccessToken";
import { StyledPopover } from "../debt_list/debtList.styled";
import RefundModal from "../apartment_cashflow_table/RefundModal";
import { StopPlansModal } from "../yearlyByMonths/StopPlansModal";
import { StyledPopoverBody } from "../expensesMonthlyNew2/MonthlyExpensesNew.styled";
import { StyledPaymentsCard } from "./PaymentsCard.styled";
import { Currency } from "../../../common/currency";

const StringUtils = BllinkUtils.Strings;
const BllinkTime = BllinkUtils.BllinkTime;
const BllinkFilter = BllinkUtils.Filter;

export function PaymentsCard({ apt, filter, buildingID, apartmentNumber, onPaymentStop, currency }) {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const client = useAccessToken();

    const ltr = useSelector(state => state.common.ltrEnabled);

    const [showPopover, setShowPopover] = useState(false);
    const [showItem, setShowItem] = useState(true);
    const [showRefundModal, setShowRefundModal] = useState(false);
    const [stopPlanContext, setStopPlanContext] = useState({ show: false });

    const redirectToConfirmationPage = () => window.open(`/status/${apt.cartID}`, '_blank');

    const date = BllinkTime.parseDate(apt?.PaymentDate);
    const amount = StringUtils.numberWithCommas(apt?.Amount);
    const tenantType = t(`reports.${apt?.TenantType}`) || '';

    const { months, oneTime } = apt;
    let text = '';
    if (Array.isArray(months)) text = shortenText(calcMonthsTextForSelectBox(months));
    if (oneTime) text = shortenText(oneTime.description);

    let comment = shortenText(apt?.Comment);

    useEffect(() => {
        const showItem = BllinkFilter.filterItems([date, apt?.ClientName, amount, apt?.CardLast4Digits, tenantType, text?.originalComment, comment, apt?.invoiceDownloadLink, apt?.translatedMethod], filter);
        setShowItem(showItem);
    }, [filter]);

    const onRefundClick = () => setShowRefundModal(true);
    const onRefundModalHide = () => setShowRefundModal(false);

    const canceledPayment = useMemo(()=> {
        return apt.Amount < 0 || constants.APIContract.paymentStates.CANCELED_PAYMENT_STATUSES.includes(apt.PaymentState);
    }, [apt]);

    function handleStopOnlineClick() {
        const paymentPlanId = apt.PaymentPlanId;
        setStopPlanContext({ show: true, buildingID, record: { apartmentNumber, paymentPlanId }, year: 'all', paymentPlanId })
    }

    function handleStopOnlineHide(stopped, record) {
        setStopPlanContext({ show: false });
        if (stopped) onPaymentStop(record);
    }

    async function handleTablesLinksClicked(e, url) {
        e.preventDefault();

        dispatch(showLoader());
        const response = await client.get(url);
        dispatch(hideLoader());
        if (response) {
            let win = window.open(response, '_blank');
            win && win.focus();
        }
    }

    function renderPopoverLinks() {
        const { isOfflinePayment, invoiceDownloadLink, PaymentState } = apt;
        let link = '';
        if (invoiceDownloadLink) {
            const url = `/managers/reports/invoice-url/${buildingID}/${invoiceDownloadLink}`;
            link = PaymentState === constants.APIContract.paymentStates.IN_PROGRESS ?
                (<OverlayTrigger
                    placement="top"
                    overlay={props => (
                        <Tooltip {...props}>
                            {t('reports.awaitingApproval')}
                        </Tooltip>
                    )}
                >
                    <div>{t('reports.downloadInvoice')}</div>
                </OverlayTrigger>) :
                <a href={url} onClick={e => handleTablesLinksClicked(e, url)}>{t('reports.downloadInvoice')}</a>
        }
                    
        return (
            <>
                <div className={`link ${apt?.cartID ? '' : 'disabled'}`} onClick={redirectToConfirmationPage}>
                    {t('reports.open_confirmation')}
                </div>
                <div className={`link ${isOfflinePayment ? 'disabled' : ''}`}>{link}</div>
                <div 
                    className={`link ${(apt?.isRefundable || apt?.isFutureCharge) && !apt?.isOfflinePayment ? '' : 'disabled'}`}
                    onClick={onRefundClick}
                >
                    {t('reports.refundPayment')}
                </div>
                <div 
                    className={`link ${apt?.isStoppableOnline ? '' : 'disabled'}`}
                    onClick={handleStopOnlineClick}
                >
                    {t('reports.stopPlan')}
                </div>           
            </>
        )
    }

    return (
        <>
            {showItem && (
                <StyledPaymentsCard ltr={ltr} canceledPayment={canceledPayment}>
                    <div className='body-card'>
                        <div>{date}</div>
                        
                        <OverlayTrigger
                            placement="top"
                            overlay={props => (
                                <Tooltip {...props}>
                                    {apt?.ClientName}
                                </Tooltip>
                            )}
                        >
                            <span className="name">{apt?.ClientName}</span>
                        </OverlayTrigger>

                        <div>{tenantType}</div>
                        <div>{amount} <Currency>{currency}</Currency></div>
                        <OverlayTrigger
                            placement="top"
                            overlay={props => (
                                <Tooltip {...props}>
                                    {text?.originalComment || text}
                                </Tooltip>
                            )}
                        >
                            <span>{text?.shortComment || text}</span>
                        </OverlayTrigger>
                        <div>{apt?.translatedMethod}</div>
        
                        <OverlayTrigger
                            placement="top"
                            overlay={props => (
                                <Tooltip {...props}>
                                    {comment?.shortComment || comment || ''}
                                </Tooltip>
                            )}
                        >
                            <span>{comment?.shortComment || comment || ''}</span>
                        </OverlayTrigger>
        
                        <div>{apt?.invoiceDownloadLink}</div>
                        <div>{apt?.CardLast4Digits}</div>
                    </div>
        
                    <OverlayTrigger
                        rootClose
                        show={showPopover}
                        onToggle={() => setShowPopover(!showPopover)}
                        trigger="click"
                        placement="bottom-start"
                        overlay={(
                            <StyledPopover>
                                <StyledPopoverBody>
                                    {renderPopoverLinks()}
                                </StyledPopoverBody>
                            </StyledPopover>
                        )
                    }>
                        <div className='tool'>
                            <div className='dot'></div>
                            <div className='dot'></div>
                            <div className='dot'></div>
                        </div>
                    </OverlayTrigger>
                </StyledPaymentsCard>
            )}

            <RefundModal
                show={showRefundModal}
                onHide={onRefundModalHide}
                paymentID={apt.PaymentId}
                paymentPlanID={apt.PaymentPlanId}
                buildingID={buildingID}
                client={client}
                isFuture={apt.isFutureCharge}
            />

            <StopPlansModal onHide={handleStopOnlineHide} {...stopPlanContext} />
        </>
    )
}
